import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Card, Col, Form, Row, Space, Input, Switch, Button, message } from 'antd';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import {
  ICitationConfiguration,
  ICitationConfigurationUpdate,
  ICitationRuleConfiguration,
} from 'common/services/configuration-service/configuration.service.types';
import configurationService from 'common/services/configuration-service/configuration.service';
import configurationStore from 'common/stores/configuration/configuration.store';
import { notification } from 'common/utils/notification';
import { EIQFormLabel } from 'common/components/label/form-label';
import CitationRuleConfiguration from './citation-rule-configuration';
import { PlusOutlined } from '@ant-design/icons';
import './citations-configurations.scss';
import { SaveOutlined } from '@ant-design/icons';

const CitationConfigurations = observer(() => {
  const [form] = Form.useForm();

  const isAutoIssueInvoice = Form.useWatch('isAutoIssueInvoice', { form });
  const isFirstCitationWarningToleranceInMPHEnabled = Form.useWatch('isFirstCitationWarningToleranceInMPHEnabled', { form });
  const isFirstCitationEscalationToleranseInMPHEnabled = Form.useWatch('isFirstCitationEscalationToleranseInMPHEnabled', { form });

  const initData = configurationStore.configuration?.citations;

  const onFinish = async (values: any) => {
    const model = mapFormValuesToModel(values);
    const result = await configurationService.updateSystemConfiguration(model);
    configurationStore.setConfiguration(result);
    notification.success({
      message: 'Configurations successfully updated',
      duration: 5,
    });
  };

  const mapFormValuesToModel = (values: any): ICitationConfigurationUpdate => {
    const model: ICitationConfigurationUpdate = {
      citations: {
        citationPaymentCommissionInPercentages: values.citationPaymentCommissionInPercentages,
        canceledCitationRemoveDelayInDays: values.canceledCitationRemoveDelayInDays,
        unassignedCitationRemoveDelayInDays: values.unassignedCitationRemoveDelayInDays,
        citationResetInDays: values.citationResetInDays,
        delinquentCitationInDays: isAutoIssueInvoice ? values.delinquentCitationInDays : null,
        isAutoSuspendTransponders: values.isAutoSuspendTransponders,
        isAutoIssueInvoice: values.isAutoIssueInvoice,
        isPreventVendorCitations: values.isPreventVendorCitations,
        firstCitationWarningToleranceInMPH: {
          value:
            values.firstCitationWarningToleranceInMPH !== '' && values.isFirstCitationWarningToleranceInMPHEnabled
              ? values.firstCitationWarningToleranceInMPH
              : null,
        },
        firstCitationEscalationToleranseInMPH: {
          value:
            values.firstCitationEscalationToleranseInMPH !== '' && values.isFirstCitationEscalationToleranseInMPHEnabled
              ? values.firstCitationEscalationToleranseInMPH
              : null,
        },
        rules: values.rules.map((item: any, index: number) => {
          const rule: ICitationRuleConfiguration = {
            order: index,
            isWarningOnly: item.isWarningOnly,
            legalNotes: item.legalNotes,
            residentFineAmount:
              item.isWarningOnly || !item.isResidentFineAmountEnabled || !item.residentFineAmount ? null : item.residentFineAmount,
            residentSuspendTransponderInDays:
              item.isWarningOnly || !item.isResidentSuspendTransponderInDaysEnabled || !item.residentSuspendTransponderInDays
                ? null
                : item.residentSuspendTransponderInDays,
            visitorFineAmount:
              item.isWarningOnly || !item.isVisitorFineAmountEnabled || !item.visitorFineAmount ? null : item.visitorFineAmount,
            visitorSuspendTransponderInDays:
              item.isWarningOnly || !item.isVisitorSuspendTransponderInDaysEnabled || !item.visitorSuspendTransponderInDays
                ? null
                : item.visitorSuspendTransponderInDays,
            revokeDrivingPrivilegesInDays:
              item.isWarningOnly || !item.isRevokeDrivingPrivilegesInDaysEnabled || !item.revokeDrivingPrivilegesInDays
                ? null
                : item.revokeDrivingPrivilegesInDays,
          };

          return rule;
        }),
      },
    };
    return model;
  };

  useEffect(() => {
    if (form && initData) {
      form.setFieldsValue(getInitData(initData));
    }
  }, [form, initData]);

  const getInitData = (configuration: ICitationConfiguration) => {
    return {
      ...configuration,
      rules: getRuleInitData(configuration.rules),
      isFirstCitationWarningToleranceInMPHEnabled: configuration.firstCitationWarningToleranceInMPH !== null ? true : false,
      isFirstCitationEscalationToleranseInMPHEnabled: configuration.firstCitationEscalationToleranseInMPH !== null ? true : false,
    };
  };

  const getRuleInitData = (rules: ICitationRuleConfiguration[]) => {
    return rules.map((ruleConfiguration) => ({
      ...ruleConfiguration,
      isResidentFineAmountEnabled: ruleConfiguration.residentFineAmount !== null ? true : false,
      isResidentSuspendTransponderInDaysEnabled: ruleConfiguration.residentSuspendTransponderInDays !== null ? true : false,
      isVisitorFineAmountEnabled: ruleConfiguration.visitorFineAmount !== null ? true : false,
      isVisitorSuspendTransponderInDaysEnabled: ruleConfiguration.visitorSuspendTransponderInDays !== null ? true : false,
      isRevokeDrivingPrivilegesInDaysEnabled: ruleConfiguration.revokeDrivingPrivilegesInDays !== null ? true : false,
    }));
  };

  return (
    <FormWrapper form={form} onFinish={onFinish} initialValues={initData} layout="vertical">
      <Card className="eiq-card eiq-card-border eiq-configuration">
        <Row className="eiq-card-header">
          <Col span={24}>
            <h1 className="h1">IntelliTicket Settings</h1>
          </Col>
        </Row>
        <Row>
          <Col xs={24} sm={12}>
            <Space className="full-width">
              <Form.Item
                name="isPreventVendorCitations"
                label={
                  <EIQFormLabel
                    title="Ignore vendor violations"
                    description="When enabled, residents won’t be cited for their vendors' traffic violations."
                  />
                }
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
            <Space className="full-width">
              <Form.Item
                name="isAutoIssueInvoice"
                label={
                  <EIQFormLabel
                    title="Send online payment links"
                    description="Violation notifications will include only fine information, they will not have any online payment links. Enable this option if you have already completed payment integration with EntranceIQ."
                  />
                }
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
            <Space className="full-width">
              <Form.Item
                name="isAutoSuspendTransponders"
                label={
                  <EIQFormLabel
                    title="Auto suspend transponders"
                    description="Violation notifications will include only transponder suspension information, they will not automatically suspend transponders."
                  />
                }
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24} sm={12}>
            <Space className="full-width">
              <Form.Item
                rules={citationValidation.unassignedCitationRemoveDelayInDays}
                name="unassignedCitationRemoveDelayInDays"
                label={
                  <EIQFormLabel
                    title="Unassigned citation removal (days)"
                    description="Days an unassigned citation remains in a citation list before being removed"
                  />
                }>
                <Input type="number" min={0} max={365} />
              </Form.Item>
            </Space>
            <Space className="full-width">
              <Form.Item
                rules={citationValidation.canceledCitationRemoveDelayInDays}
                name="canceledCitationRemoveDelayInDays"
                label={
                  <EIQFormLabel
                    title="Canceled citation removal (days)"
                    description="Days a canceled citation remains in a citation list before being removed"
                  />
                }>
                <Input type="number" min={0} max={365} />
              </Form.Item>
            </Space>
            <Space className="full-width" style={{ flexDirection: 'column' }}>
              <Form.Item
                rules={citationValidation.citationResetInDays}
                name="citationResetInDays"
                label={
                  <EIQFormLabel
                    title="Citation reset (days)"
                    description="Violations reset after the days set here unless there is a new offence. (Counts all violations unless it is cancelled)"
                  />
                }>
                <Input type="number" min={0} max={365} />
              </Form.Item>
            </Space>
            <Space className="full-width" style={{ flexDirection: 'column' }}>
              <Form.Item
                rules={isAutoIssueInvoice ? citationValidation.delinquentCitationInDays : undefined}
                name="delinquentCitationInDays"
                label={
                  <EIQFormLabel
                    title="Delinquent payments (days)"
                    description="Suspend all transponders of the resident for unpaid citations after the days set here."
                  />
                }>
                <Input type="number" min={0} max={365} disabled={!isAutoIssueInvoice} />
              </Form.Item>
            </Space>
            <Space className="full-width" style={{ flexDirection: 'column' }}>
              <div className="resident-section-feilds-item">
                <Form.Item name="isFirstCitationWarningToleranceInMPHEnabled" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item
                  name="firstCitationWarningToleranceInMPH"
                  label={
                    <EIQFormLabel
                      title="First Citation Warning Tolerance (mph)"
                      description="The first offence is a warning unless this speed is exceeded — then penalties apply. "
                    />
                  }>
                  <Input type="number" min={0} max={300} disabled={!isFirstCitationWarningToleranceInMPHEnabled} />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width" style={{ flexDirection: 'column' }}>
              <div className="resident-section-feilds-item">
                <Form.Item name="isFirstCitationEscalationToleranseInMPHEnabled" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item
                  name="firstCitationEscalationToleranseInMPH"
                  label={
                    <EIQFormLabel
                      title="First Citation Escalation Tolerance (mph)"
                      description="If the recorded speed exceeds the set tolerance, the first offense is escalated to a second-level citation."
                    />
                  }>
                  <Input type="number" min={0} max={300} disabled={!isFirstCitationEscalationToleranseInMPHEnabled} />
                </Form.Item>
              </div>
            </Space>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <div className="citation-levels-title">Citation Levels & Penalties</div>
            <Form.List name="rules">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <CitationRuleConfiguration key={key} index={index} name={name} restField={restField} remove={remove} form={form} />
                  ))}

                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add rule
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
            <div className="save-citation-configuration-btn">
              <Button htmlType="submit" type="primary" icon={<SaveOutlined />} style={{ width: '100%' }}>
                Save
              </Button>
            </div>
          </Col>
        </Row>
      </Card>
    </FormWrapper>
  );
});

export default CitationConfigurations;

export const citationValidation = {
  unassignedCitationRemoveDelayInDays: [{ required: true, message: 'Unassigned citation remove delay in days is required!' }],
  canceledCitationRemoveDelayInDays: [{ required: true, message: 'Canceled citation remove delay in days is required!' }],
  delinquentCitationInDays: [{ required: true, message: 'Delinquent citation in days is required!' }],
  citationResetInDays: [{ required: true, message: 'Citation reset in days  is required!' }],
};
